import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { useHapticFeedback } from '@blade/ui-kit'
import {
  CheckCurrencyHero,
  CheckCurrencyProducts,
  checkCurrencyReatom,
} from '@root/features/premium/check-currency'
import { PremiumInfoDrawer } from '@root/features/premium/premium-information/ui/mobile'
import { DomUtil } from '@root/shared'
import { useBreakpoint } from '@root/shared/hooks/useBreakpoint'
import BG_GRID from '@root/widgets/desktop/premium-purchase/assets/premium-bg.inline.svg'

import { PremiumScreenHeading } from './premium-screen-heading'
import { PremiumScreenResultDrawer } from './premium-screen-result-drawer'
import { PremiumScreenSlider } from './premium-screen-slider'

type Props = {}
export const PremiumScreen: React.FC<Props> = () => {
  const { t } = useTranslation()
  const breakpoint = useBreakpoint()

  const [subscriptionInfoDrawerOpen, setSubscriptionInfoDrawerOpen] =
    useState(false)
  const [boosterInfoDrawerOpen, setBoostersInfoDrawerOpen] = useState(false)
  const haptics = useHapticFeedback()

  const model = useMemo(
    () => checkCurrencyReatom(undefined, { name: 'premiumScreen' }),
    [],
  )

  return (
    <Root>
      <GlowingStart />
      <GlowingEnd />
      <Content>
        <PremiumCentered>
          <Heading>
            <PremiumScreenHeading />
            <Search>
              <Title>{t('premium.search.title')}</Title>
              <CheckCurrencyHero model={model} />
            </Search>
          </Heading>
          <PremiumScreenSlider mode="single" />
        </PremiumCentered>
        <Actions>
          <CheckCurrencyProducts
            device={breakpoint}
            onBoostersInfoClick={() => {
              haptics.impactOccurred('light')
              setBoostersInfoDrawerOpen(true)
            }}
            onSubscriptionInfoClick={() => {
              haptics.impactOccurred('light')
              setSubscriptionInfoDrawerOpen(true)
            }}
          />
        </Actions>
      </Content>
      <PremiumInfoDrawer
        open={boosterInfoDrawerOpen}
        onOpenChange={setBoostersInfoDrawerOpen}
        product="boosters"
      />
      <PremiumInfoDrawer
        open={subscriptionInfoDrawerOpen}
        onOpenChange={setSubscriptionInfoDrawerOpen}
        product="subscription"
      />
      <PremiumScreenResultDrawer model={model} />
    </Root>
  )
}

const Root = styled.div`
  position: relative;
  background: var(--appearance-1);
  height: 100%;
`

const Content = styled.div`
  overflow: auto;
  display: grid;
  height: 100%;
  padding-block: 16px;
  padding-inline: 12px;
  grid-template-rows: 1fr auto;

  background-image: ${DomUtil.cssUrl(BG_GRID)};
  background-repeat: no-repeat;
  background-position: 100% 100%;
`

const Actions = styled.header`
  display: grid;
  position: relative;

  border-radius: 22px;
  border: 1px solid var(--backgroundA-4);
  padding: 4px;
`

const PremiumCentered = styled.div`
  display: grid;
  gap: 20px;
  align-self: center;
  width: 100%;
  padding-top: 80px;
`

const Heading = styled.div`
  display: grid;
  position: relative;
`

const Search = styled.div`
  border-radius: 14px;
  border: 1px solid rgba(var(--appearance-inv-1-rgb), 0.1);
  background: rgba(var(--appearance-inv-1-rgb), 0.07);
  backdrop-filter: blur(17.5px);

  display: grid;
  padding: 14px;
  gap: 10px;
  flex-shrink: 0;
`

const Title = styled.div`
  color: var(--text-12);
  text-align: center;
`

const GlowingStart = styled.span`
  background-image: radial-gradient(
    50% 370px at 110px 60%,
    var(--subscriptionGradientFromA-4) 0%,
    transparent 100%
  );
  pointer-events: none;
  position: absolute;
  left: 0;
  bottom: 0;
  right: 0;
  top: 0;
  opacity: 0.5;

  &::before {
    content: '';

    opacity: 0.7;
    position: absolute;

    top: 25%;
    left: -15%;
    width: 100%;
    height: 100%;
    background-image: radial-gradient(
      50% 25% at 50% 70%,
      var(--subscriptionGradientFromA-9),
      transparent
    );

    transform: rotate(45deg);
  }
`

const GlowingEnd = styled.span`
  background-image: radial-gradient(
    50% 60% at 85% 100%,
    var(--subscriptionGradientToA-3) 0%,
    transparent 100%
  );
  pointer-events: none;

  position: absolute;
  left: 0;
  bottom: 0;
  right: 0;
  top: 0;
  opacity: 0.8;

  &::before {
    content: '';
    opacity: 0.8;
    position: absolute;
    top: -31%;
    left: -20%;
    width: 200%;
    height: 130%;
    background-image: radial-gradient(
      50% 25% at 50% 70%,
      var(--subscriptionGradientFromA-8),
      transparent
    );
    transform: rotate(45deg);
  }
`
